import { Icon } from '@iconify/react';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import VenuePreviewModal from './VenuePreviewModal';
import ControlTowerMap from './child/ControlTowerMap';

const AllVenuesLayer = () => {
    const { auth } = useAuth();

    const [viewMode, setViewMode] = useState('table'); // 'table' | 'map'
    const [venues, setVenues] = useState([]);
    const [categories, setCategories] = useState([]);
    const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
    const [loading, setLoading] = useState(true);

    // Filters & Search
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [cityFilter, setCityFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1, limit: 15 });

    // Bulk Selection State
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [showBulkEdit, setShowBulkEdit] = useState(false);
    const [bulkEditFields, setBulkEditFields] = useState({ business_category: '', city: '', state: '', country: '', currency: '' });
    const [isBulkEditing, setIsBulkEditing] = useState(false);

    // Live Preview Modal
    const [showPreview, setShowPreview] = useState(false);
    const [previewVenueId, setPreviewVenueId] = useState(null);

    // Bulk Import CSV State
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);

    // Active Row Dropdown State
    const [activeDropdownId, setActiveDropdownId] = useState(null);

    // Fetch Stats & Categories
    const fetchMeta = useCallback(async () => {
        try {
            const [statsRes, catRes] = await Promise.all([
                auth.fetch(`${config.API_BASE_URL}/api/admin/venues/stats`),
                auth.fetch(`${config.API_BASE_URL}/api/categories`)
            ]);

            if (statsRes.ok) {
                const statsData = await statsRes.json();
                setStats(statsData);
            }

            if (catRes.ok) {
                const catData = await catRes.json();
                setCategories(Array.isArray(catData) ? catData : (catData.data || []));
            }
        } catch (error) {
            console.error('Error fetching venue meta:', error);
        }
    }, [auth]);

    // Fetch Venues List
    const fetchVenues = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                status: statusFilter === 'all' ? '' : statusFilter,
                search: searchTerm,
                page: page,
                limit: pagination.limit
            });

            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues?${query}`);
            if (res.ok) {
                const data = await res.json();
                setVenues(data.data || []);
                if (data.pagination) {
                    setPagination(data.pagination);
                }
            } else {
                toast.error('Failed to load venues');
            }
        } catch (error) {
            console.error('Error fetching venues:', error);
            toast.error('Error connecting to venues API');
        } finally {
            setLoading(false);
        }
    }, [auth, statusFilter, searchTerm, page, pagination.limit]);

    useEffect(() => {
        fetchMeta();
    }, [fetchMeta]);

    useEffect(() => {
        fetchVenues();
    }, [fetchVenues]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (!e.target.closest('.dropdown')) {
                setActiveDropdownId(null);
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, []);

    // Client-side category & city filter
    const displayedVenues = useMemo(() => {
        return venues.filter(v => {
            if (categoryFilter !== 'all' && v.category_name !== categoryFilter) {
                return false;
            }
            if (cityFilter !== 'all' && v.city !== cityFilter) {
                return false;
            }
            return true;
        });
    }, [venues, categoryFilter, cityFilter]);

    // Unique cities from venues
    const uniqueCities = useMemo(() => {
        const set = new Set();
        venues.forEach(v => { if (v.city) set.add(v.city); });
        return Array.from(set);
    }, [venues]);

    // Selection Handlers
    const toggleSelectAll = () => {
        if (selectedIds.length === displayedVenues.length && displayedVenues.length > 0) {
            setSelectedIds([]);
        } else {
            setSelectedIds(displayedVenues.map(v => v.id));
        }
    };

    const toggleSelectOne = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const clearSelection = () => {
        setSelectedIds([]);
    };

    // Bulk Delete Action
    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;

        const count = selectedIds.length;
        const confirmMsg = `Are you sure you want to permanently delete ${count} selected venue${count > 1 ? 's' : ''}? This action cannot be undone.`;

        if (!window.confirm(confirmMsg)) return;

        setIsBulkDeleting(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/bulk-delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: selectedIds })
            });

            const data = await res.json();

            if (res.ok) {
                toast.success(`${count} venue${count > 1 ? 's' : ''} deleted successfully`);
                setSelectedIds([]);
                fetchVenues();
                fetchMeta();
            } else {
                toast.error(data.error || 'Failed to delete selected venues');
            }
        } catch (err) {
            console.error('Bulk delete error:', err);
            toast.error('Error during bulk deletion');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // Bulk Edit: only fields that were filled in are changed
    const handleBulkEdit = async () => {
        const changes = Object.fromEntries(
            Object.entries(bulkEditFields).map(([key, value]) => [key, value.trim()]).filter(([, value]) => value)
        );
        if (Object.keys(changes).length === 0) {
            toast.error('Fill in at least one field to change');
            return;
        }
        setIsBulkEditing(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/bulk-edit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ venues: selectedIds.map(id => ({ id, ...changes })) })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Bulk edit failed');
            const { updated = 0, errors = [] } = data.results || {};
            if (errors.length > 0) {
                toast.warning(`${updated} updated, ${errors.length} failed: ${errors[0]}`);
            } else {
                toast.success(`${updated} venue${updated === 1 ? '' : 's'} updated`);
            }
            setShowBulkEdit(false);
            setBulkEditFields({ business_category: '', city: '', state: '', country: '', currency: '' });
            setSelectedIds([]);
            fetchVenues();
            fetchMeta();
        } catch (err) {
            console.error('Bulk edit error:', err);
            toast.error(err.message || 'Bulk edit failed');
        } finally {
            setIsBulkEditing(false);
        }
    };

    // Bulk Status Change
    const handleBulkStatusChange = async (newStatus) => {
        if (selectedIds.length === 0) return;

        const count = selectedIds.length;
        if (!window.confirm(`Mark ${count} selected venue${count > 1 ? 's' : ''} as ${newStatus}?`)) return;

        try {
            const promises = selectedIds.map(id => 
                auth.fetch(`${config.API_BASE_URL}/api/admin/venues/${id}/verification`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ status: newStatus, notes: `Bulk update to ${newStatus} by admin` })
                })
            );

            await Promise.all(promises);
            toast.success(`Updated ${count} venue${count > 1 ? 's' : ''} to ${newStatus}`);
            setSelectedIds([]);
            fetchVenues();
            fetchMeta();
        } catch (err) {
            console.error('Bulk status error:', err);
            toast.error('Error updating venue statuses');
        }
    };

    const handlePreview = (venueId) => {
        setPreviewVenueId(venueId);
        setShowPreview(true);
    };

    const handleStatusChange = async (venueId, newStatus) => {
        if (!window.confirm(`Are you sure you want to mark this venue as ${newStatus}?`)) return;

        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/${venueId}/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus, notes: `Status updated to ${newStatus} by admin` })
            });

            if (res.ok) {
                toast.success(`Venue status updated to ${newStatus}`);
                fetchVenues();
                fetchMeta();
            } else {
                const err = await res.json();
                toast.error(err.error || 'Failed to update status');
            }
        } catch (e) {
            toast.error('Error updating venue status');
        }
    };

    const handleDeleteVenue = async (venue) => {
        if (!window.confirm(`Permanently delete "${venue.title || venue.business_name}"? This action cannot be undone.`)) return;

        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venue.id}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                toast.success('Venue deleted successfully');
                setSelectedIds(prev => prev.filter(id => id !== venue.id));
                fetchVenues();
                fetchMeta();
            } else {
                toast.error('Failed to delete venue');
            }
        } catch (e) {
            toast.error('Error deleting venue');
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '/assets/images/placeholder.png';
        if (path.startsWith('http')) return path;
        return `${config.API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    const getPriceLevelLabel = (tier) => {
        switch (tier) {
            case '$': return 'Budget';
            case '$$': return 'Moderate';
            case '$$$': return 'Fine Dining';
            case '$$$$': return 'Luxury / VIP';
            default: return tier || 'Moderate';
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <span className="badge bg-success-focus text-success-main px-12 py-6 rounded-pill fw-semibold text-xs d-inline-flex align-items-center gap-1"><Icon icon="solar:check-circle-bold" /> Approved</span>;
            case 'rejected':
                return <span className="badge bg-danger-focus text-danger-main px-12 py-6 rounded-pill fw-semibold text-xs d-inline-flex align-items-center gap-1"><Icon icon="solar:close-circle-bold" /> Rejected</span>;
            case 'suspended':
                return <span className="badge bg-secondary-focus text-secondary-main px-12 py-6 rounded-pill fw-semibold text-xs d-inline-flex align-items-center gap-1"><Icon icon="solar:lock-bold" /> Suspended</span>;
            default:
                return <span className="badge bg-warning-focus text-warning-main px-12 py-6 rounded-pill fw-semibold text-xs d-inline-flex align-items-center gap-1"><Icon icon="solar:clock-circle-bold" /> Pending</span>;
        }
    };

    const isAllSelected = displayedVenues.length > 0 && selectedIds.length === displayedVenues.length;

    return (
        <div className="d-flex flex-column gap-4">
            {/* Top Stat Cards - Clean white with colored top accent line */}
            <div className="row g-3">
                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="card shadow-sm radius-12 p-20 bg-white h-100 position-relative overflow-hidden" style={{ border: '1px solid var(--hs-border)', borderTop: '3px solid #0388d6' }}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <p className="fw-medium text-secondary-light text-xs text-uppercase letter-spacing-1 mb-1">Total Venues</p>
                                <h3 className="fw-bold mb-0 text-dark">{stats.total || venues.length}</h3>
                            </div>
                            <div className="w-48-px h-48-px rounded-12 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f0f9ff', color: '#0388d6' }}>
                                <Icon icon="solar:buildings-bold" className="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="card shadow-sm radius-12 p-20 bg-white h-100 position-relative overflow-hidden" style={{ border: '1px solid var(--hs-border)', borderTop: '3px solid #10B981' }}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <p className="fw-medium text-secondary-light text-xs text-uppercase letter-spacing-1 mb-1">Approved Venues</p>
                                <h3 className="fw-bold mb-0 text-dark">{stats.approved || 0}</h3>
                            </div>
                            <div className="w-48-px h-48-px rounded-12 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#ecfdf5', color: '#10B981' }}>
                                <Icon icon="solar:check-circle-bold" className="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="card shadow-sm radius-12 p-20 bg-white h-100 position-relative overflow-hidden" style={{ border: '1px solid var(--hs-border)', borderTop: '3px solid #F59E0B' }}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <p className="fw-medium text-secondary-light text-xs text-uppercase letter-spacing-1 mb-1">Pending Verification</p>
                                <h3 className="fw-bold mb-0 text-dark">{stats.pending || 0}</h3>
                            </div>
                            <div className="w-48-px h-48-px rounded-12 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#fffbeb', color: '#F59E0B' }}>
                                <Icon icon="solar:clock-circle-bold" className="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="card shadow-sm radius-12 p-20 bg-white h-100 position-relative overflow-hidden" style={{ border: '1px solid var(--hs-border)', borderTop: '3px solid #EF4444' }}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <p className="fw-medium text-secondary-light text-xs text-uppercase letter-spacing-1 mb-1">Rejected / Suspended</p>
                                <h3 className="fw-bold mb-0 text-dark">{stats.rejected || 0}</h3>
                            </div>
                            <div className="w-48-px h-48-px rounded-12 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#fef2f2', color: '#EF4444' }}>
                                <Icon icon="solar:close-circle-bold" className="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bulk Actions Floating Banner (When items are selected) */}
            {selectedIds.length > 0 && (
                <div className="card border-0 shadow-lg radius-12 bg-dark text-white p-16 animate__animated animate__fadeInDown">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="w-36-px h-36-px rounded-circle bg-primary-600 text-white d-flex align-items-center justify-content-center fw-bold">
                                {selectedIds.length}
                            </div>
                            <div>
                                <h6 className="mb-0 text-white fw-bold">
                                    {selectedIds.length} Venue{selectedIds.length > 1 ? 's' : ''} Selected
                                </h6>
                                <span className="text-xs text-white opacity-75">Perform bulk actions across all selected venues</span>
                            </div>
                        </div>

                        <div className="d-flex align-items-center flex-wrap gap-2">
                            {/* Bulk Delete Button */}
                            <button 
                                className="btn btn-danger btn-sm radius-8 px-16 py-8 d-flex align-items-center gap-2 fw-semibold shadow"
                                onClick={handleBulkDelete}
                                disabled={isBulkDeleting}
                            >
                                <Icon icon="solar:trash-bin-trash-bold" className="text-lg" />
                                <span>{isBulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.length})`}</span>
                            </button>

                            {/* Bulk Edit Button */}
                            <button
                                className="btn btn-primary btn-sm radius-8 px-14 py-8 d-flex align-items-center gap-2"
                                onClick={() => setShowBulkEdit(true)}
                            >
                                <Icon icon="solar:pen-bold" />
                                <span>Bulk Edit</span>
                            </button>

                            {/* Bulk Approve Button */}
                            <button 
                                className="btn btn-success btn-sm radius-8 px-14 py-8 d-flex align-items-center gap-1 fw-semibold"
                                onClick={() => handleBulkStatusChange('approved')}
                            >
                                <Icon icon="solar:check-circle-bold" />
                                <span>Approve All</span>
                            </button>

                            {/* Bulk Suspend Button */}
                            <button 
                                className="btn btn-warning btn-sm radius-8 px-14 py-8 d-flex align-items-center gap-1 fw-semibold text-dark"
                                onClick={() => handleBulkStatusChange('suspended')}
                            >
                                <Icon icon="solar:lock-bold" />
                                <span>Suspend All</span>
                            </button>

                            {/* Clear Selection */}
                            <button 
                                className="btn btn-outline-light btn-sm radius-8 px-12 py-8"
                                onClick={clearSelection}
                            >
                                Deselect All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main All Venues Directory Card */}
            <div className="card h-100 p-0 radius-12 shadow-sm border-0">
                {/* Header, Search & Filters */}
                <div className="card-header border-bottom bg-white py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div>
                        <h6 className="text-lg fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                            <Icon icon="solar:buildings-3-bold" className="text-primary-600" />
                            All Venues Directory
                        </h6>
                        <span className="text-xs text-secondary-light">Manage, inspect, bulk delete, and preview all registered venues</span>
                    </div>

                    {/* Filter Controls */}
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        {/* Status Filter */}
                        <select 
                            className="form-select form-select-sm w-auto bg-base radius-8"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        >
                            <option value="all">All Statuses</option>
                            <option value="approved">Approved</option>
                            <option value="pending">Pending</option>
                            <option value="rejected">Rejected</option>
                            <option value="suspended">Suspended</option>
                        </select>

                        {/* Category Filter */}
                        <select 
                            className="form-select form-select-sm w-auto bg-base radius-8"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>

                        {/* City Filter */}
                        {uniqueCities.length > 0 && (
                            <select 
                                className="form-select form-select-sm w-auto bg-base radius-8"
                                value={cityFilter}
                                onChange={(e) => setCityFilter(e.target.value)}
                            >
                                <option value="all">All Cities</option>
                                {uniqueCities.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        )}

                        {/* Search Input */}
                        <div className="navbar-search position-relative">
                            <input 
                                type="text" 
                                className="bg-base h-40-px w-auto radius-8 form-control ps-36 pe-12" 
                                placeholder="Search venue, owner, email..." 
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            />
                            <Icon icon="ion:search-outline" className="position-absolute start-0 top-50 translate-middle-y ms-12 text-secondary-light text-lg" />
                            {searchTerm && (
                                <button 
                                    className="btn btn-sm btn-link position-absolute end-0 top-50 translate-middle-y text-secondary me-4 p-0 text-decoration-none"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <Icon icon="mdi:close-circle" />
                                </button>
                            )}
                        </div>

                        {/* Bulk Import CSV Button */}
                        <button 
                            className="btn btn-sm text-white radius-8 d-flex align-items-center gap-1.5 shadow-none"
                            style={{ backgroundColor: '#0388d6', border: 'none' }}
                            onClick={() => { setShowBulkImport(true); setImportResult(null); setCsvFile(null); }}
                            title="Bulk import venues from a CSV file"
                        >
                            <Icon icon="solar:upload-bold" />
                            <span>Import CSV</span>
                        </button>

                        {/* Refresh Button */}
                        <button 
                            className="btn btn-sm btn-outline-secondary radius-8 d-flex align-items-center gap-1"
                            onClick={() => { fetchVenues(); fetchMeta(); }}
                            title="Refresh List"
                        >
                            <Icon icon="solar:refresh-bold" />
                        </button>

                        {/* View Mode Switcher: Table vs Map Preview */}
                        <div className="btn-group btn-group-sm radius-8 border ms-1">
                            <button
                                type="button"
                                className={`btn btn-sm ${viewMode === 'table' ? 'btn-primary' : 'btn-light text-secondary'} d-inline-flex align-items-center gap-1`}
                                onClick={() => setViewMode('table')}
                                title="Table Directory View"
                            >
                                <Icon icon="solar:list-bold" />
                                <span>Table</span>
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${viewMode === 'map' ? 'btn-primary' : 'btn-light text-secondary'} d-inline-flex align-items-center gap-1`}
                                onClick={() => setViewMode('map')}
                                title="Live Map Preview of Venues"
                            >
                                <Icon icon="solar:map-point-wave-bold" />
                                <span>Map Preview ({displayedVenues.length})</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Card Content: Map Preview or Table */}
                {viewMode === 'map' ? (
                    <div className="card-body p-16">
                        <ControlTowerMap 
                            venues={displayedVenues} 
                            height="580px" 
                            title={`Venues Map Preview (${displayedVenues.length} Venues)`} 
                        />
                    </div>
                ) : (
                    <div className="card-body p-24">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading venues...</span>
                            </div>
                            <p className="text-secondary mt-2">Loading venues directory...</p>
                        </div>
                    ) : displayedVenues.length === 0 ? (
                        <div className="text-center py-5 text-secondary">
                            <Icon icon="solar:buildings-2-bold-duotone" className="text-5xl text-secondary-light mb-3" />
                            <h6 className="fw-semibold text-dark">No venues found</h6>
                            <p className="text-sm text-secondary-light">
                                {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                                    ? 'Try adjusting your search criteria or filter options.'
                                    : 'There are no registered venues in the database.'}
                            </p>
                        </div>
                    ) : (
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0 align-middle">
                                <thead>
                                    <tr className="bg-light">
                                        {/* Select All Checkbox */}
                                        <th scope="col" className="py-12 text-center" style={{ width: '44px' }}>
                                            <input 
                                                type="checkbox"
                                                className="form-check-input cursor-pointer"
                                                checked={isAllSelected}
                                                onChange={toggleSelectAll}
                                                title="Select / Deselect All on this page"
                                            />
                                        </th>
                                        <th scope="col" className="py-12">Venue</th>
                                        <th scope="col" className="py-12">Category & Price</th>
                                        <th scope="col" className="py-12">Owner / Contact</th>
                                        <th scope="col" className="py-12">Location</th>
                                        <th scope="col" className="py-12 text-center">Status</th>
                                        <th scope="col" className="py-12 text-center">Live Preview</th>
                                        <th scope="col" className="py-12 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedVenues.map(venue => {
                                        const isSelected = selectedIds.includes(venue.id);
                                        return (
                                            <tr key={venue.id} className={isSelected ? 'table-primary-subtle' : ''}>
                                                {/* Checkbox */}
                                                <td className="text-center">
                                                    <input 
                                                        type="checkbox"
                                                        className="form-check-input cursor-pointer"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelectOne(venue.id)}
                                                    />
                                                </td>

                                                {/* Venue Info */}
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <img 
                                                            src={getImageUrl(venue.thumbnail || venue.logo)} 
                                                            alt={venue.title || venue.business_name}
                                                            className="w-48-px h-48-px rounded-3 object-fit-cover border shadow-2xs"
                                                            onError={(e) => {
                                                                e.currentTarget.onerror = null;
                                                                e.currentTarget.src = '/assets/images/placeholder.png';
                                                            }}
                                                        />
                                                        <div>
                                                            <h6 className="text-md mb-0 fw-bold text-dark">
                                                                {venue.title || venue.business_name || 'Untitled Venue'}
                                                            </h6>
                                                            <span className="text-xs text-secondary-light fw-medium">
                                                                ID: #{venue.id} {venue.license_number ? `• Lic: ${venue.license_number}` : ''}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Category & Pricing */}
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        {venue.category_name ? (
                                                            <span className="badge bg-primary-50 text-primary-700 rounded-pill w-auto align-self-start fw-semibold text-xs">
                                                                <Icon icon="solar:tag-bold" className="me-1" />
                                                                {venue.category_name}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-secondary-light">Uncategorized</span>
                                                        )}
                                                        <span className="text-xs text-dark fw-medium">
                                                            {venue.average_price_for_two > 0 
                                                                ? `Avg: ${venue.average_price_for_two} AED (2p)` 
                                                                : `${getPriceLevelLabel(venue.price_level)} (AED)`}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Owner & Contact */}
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        <span className="text-sm fw-semibold text-dark">
                                                            {venue.owner_name || 'Business Owner'}
                                                        </span>
                                                        <span className="text-xs text-secondary-light text-break">
                                                            {venue.owner_email || venue.email || 'No email'}
                                                        </span>
                                                        {venue.phone && (
                                                            <span className="text-xs text-secondary-light">
                                                                <Icon icon="solar:phone-bold" className="me-1" />
                                                                {venue.phone}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Location */}
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        <span className="text-sm fw-medium text-dark d-flex align-items-center gap-1">
                                                            <Icon icon="solar:map-point-bold" className="text-primary-600" />
                                                            {venue.city || 'Dubai'}, {venue.country || 'UAE'}
                                                        </span>
                                                        {venue.address && (
                                                            <span className="text-xs text-secondary-light text-truncate" style={{ maxWidth: '180px' }} title={venue.address}>
                                                                {venue.address}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Status Badge */}
                                                <td className="text-center">
                                                    {getStatusBadge(venue.verification_status)}
                                                    <span className="d-flex align-items-center justify-content-center gap-1 text-xs text-secondary-light mt-1" title="Likes from mobile app users">
                                                        <Icon icon="solar:heart-bold" className="text-danger-600" />
                                                        {venue.likes_count || 0}
                                                    </span>
                                                </td>

                                                {/* Live Preview Button */}
                                                <td className="text-center">
                                                    <button 
                                                        className="btn btn-sm btn-primary radius-8 px-12 py-6 d-inline-flex align-items-center gap-1 shadow-2xs"
                                                        onClick={() => handlePreview(venue.id)}
                                                        title="Preview Customer Experience"
                                                    >
                                                        <Icon icon="solar:eye-bold" /> Preview
                                                    </button>
                                                </td>

                                                {/* Action Dropdown */}
                                                <td className="text-center">
                                                    <div className="dropdown position-relative">
                                                        <button 
                                                            className="btn btn-sm btn-outline-secondary radius-8 px-10 py-6 d-flex align-items-center gap-1 mx-auto" 
                                                            type="button" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveDropdownId(prev => prev === venue.id ? null : venue.id);
                                                            }}
                                                        >
                                                            <Icon icon="solar:menu-dots-bold" />
                                                        </button>
                                                        {activeDropdownId === venue.id && (
                                                            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0 radius-8 show position-absolute end-0 mt-1 d-block" style={{ zIndex: 1050 }}>
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item d-flex align-items-center gap-2 py-8 text-primary-600" 
                                                                        onClick={() => { setActiveDropdownId(null); handlePreview(venue.id); }}
                                                                    >
                                                                        <Icon icon="solar:eye-bold" /> Live Preview
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item d-flex align-items-center gap-2 py-8 text-success-main" 
                                                                        onClick={() => { setActiveDropdownId(null); handleStatusChange(venue.id, 'approved'); }}
                                                                    >
                                                                        <Icon icon="solar:check-circle-bold" /> Approve Venue
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item d-flex align-items-center gap-2 py-8 text-warning-main" 
                                                                        onClick={() => { setActiveDropdownId(null); handleStatusChange(venue.id, 'suspended'); }}
                                                                    >
                                                                        <Icon icon="solar:lock-bold" /> Suspend Venue
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item d-flex align-items-center gap-2 py-8 text-danger-main" 
                                                                        onClick={() => { setActiveDropdownId(null); handleStatusChange(venue.id, 'rejected'); }}
                                                                    >
                                                                        <Icon icon="solar:close-circle-bold" /> Reject Venue
                                                                    </button>
                                                                </li>
                                                                <li><hr className="dropdown-divider my-1" /></li>
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item d-flex align-items-center gap-2 py-8 text-danger-main" 
                                                                        onClick={() => { setActiveDropdownId(null); handleDeleteVenue(venue); }}
                                                                    >
                                                                        <Icon icon="solar:trash-bin-trash-bold" /> Delete Venue
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                )}
            </div>

            {/* Live Venue Preview Modal */}
            <VenuePreviewModal 
                show={showPreview}
                onHide={() => setShowPreview(false)}
                venueId={previewVenueId}
            />

            {/* ── Bulk Edit Modal ── */}
            {showBulkEdit && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header border-bottom">
                                <h5 className="modal-title">Bulk Edit {selectedIds.length} Venue{selectedIds.length > 1 ? 's' : ''}</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowBulkEdit(false)} />
                            </div>
                            <div className="modal-body">
                                <p className="text-sm text-secondary-light mb-16">Fields left blank are not changed.</p>
                                {[
                                    ['business_category', 'Business Category'],
                                    ['city', 'City'],
                                    ['state', 'State / Emirate'],
                                    ['country', 'Country'],
                                    ['currency', 'Currency'],
                                ].map(([field, label]) => (
                                    <div className="mb-12" key={field}>
                                        <label htmlFor={`bulk-edit-${field}`} className="form-label text-sm fw-semibold mb-4">{label}</label>
                                        <input
                                            id={`bulk-edit-${field}`}
                                            type="text"
                                            className="form-control form-control-sm radius-8"
                                            value={bulkEditFields[field]}
                                            onChange={(e) => setBulkEditFields(prev => ({ ...prev, [field]: e.target.value }))}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="modal-footer border-top">
                                <button type="button" className="hs-btn-secondary" onClick={() => setShowBulkEdit(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary btn-sm" disabled={isBulkEditing} onClick={handleBulkEdit}>
                                    {isBulkEditing ? 'Saving...' : 'Apply Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Bulk Import CSV Modal ── */}
            {showBulkImport && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header border-bottom">
                                <h5 className="modal-title d-flex align-items-center gap-2">
                                    <Icon icon="mdi:file-upload-outline" className="text-primary-600 text-xl" />
                                    Bulk Import Venues (CSV)
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowBulkImport(false)} />
                            </div>
                            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                <div className="alert alert-info d-flex gap-3 align-items-start mb-16" role="alert">
                                    <Icon icon="mdi:information-outline" className="text-xl mt-1 flex-shrink-0" />
                                    <div style={{ fontSize: '12px' }}>
                                        <p className="fw-semibold mb-6">CSV Column Order (first row = header):</p>
                                        <div className="row g-2">
                                            <div className="col-md-6">
                                                <p className="fw-medium mb-2 text-primary-600">Venue Core (required)</p>
                                                <code style={{ fontSize: '10px', display: 'block', whiteSpace: 'pre-wrap' }}>
{`owner_email, owner_full_name, owner_phone
venue_business_name, venue_title, venue_description
venue_category, venue_email, venue_address
venue_city, venue_state, venue_country
venue_latitude, venue_longitude, venue_image_url
plan_type (premium | basic | free)
subscription_status (active | canceled | past_due)`}
                                                </code>
                                            </div>
                                            <div className="col-md-6">
                                                <p className="fw-medium mb-2 text-success-main">Features &amp; Menus (optional)</p>
                                                <code style={{ fontSize: '10px', display: 'block', whiteSpace: 'pre-wrap' }}>
{`feature_1_name, feature_1_price, feature_1_description
menu_category, menu_item_1_name, menu_item_1_price
venue_price_level ($, $$, $$$, $$$$)
venue_avg_price_for_two, venue_currency`}
                                                </code>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-16">
                                    <a
                                        href={`data:text/csv;charset=utf-8,owner_email,owner_full_name,owner_phone,venue_business_name,venue_title,venue_description,venue_category,venue_email,venue_address,venue_city,venue_state,venue_country,venue_latitude,venue_longitude,venue_image_url,plan_type,subscription_status,feature_1_name,feature_1_price,feature_1_description,feature_2_name,feature_2_price,feature_2_description,feature_3_name,feature_3_price,feature_3_description,feature_4_name,feature_4_price,feature_4_description,feature_5_name,feature_5_price,feature_5_description,menu_category,menu_item_1_name,menu_item_1_price,menu_item_1_description,menu_item_2_name,menu_item_2_price,menu_item_2_description,menu_item_3_name,menu_item_3_price,menu_item_3_description,menu_item_4_name,menu_item_4_price,menu_item_4_description,menu_item_5_name,menu_item_5_price,menu_item_5_description,venue_price_level,venue_avg_price_for_two,venue_currency,entertainment_type,entertainment_name,entertainment_description,entertainment_price,entertainment_schedule%0Aowner1%40hubscroll.com,Owner One,%2B971501234567,Burj Lounge,Burj Lounge,A premium lounge,Lounge,lounge%40hubscroll.com,Burj Khalifa,Dubai,Dubai,UAE,25.1972,55.2744,,premium,active,VIP Area,200,Access to private lounge area,Valet Parking,50,Premium valet service,,,,,,,,,Beverages,Cappuccino,28,Premium roasted coffee,Green Tea,22,Organic Japanese sencha,,,,,,,,%24%24%24,300,AED,live_music,Jazz Night,Live acoustic jazz band,0,Fridays 9 PM - 12 AM`}
                                        download="bulk_import_template.csv"
                                        className="btn btn-sm btn-secondary-600 d-inline-flex align-items-center gap-2"
                                    >
                                        <Icon icon="mdi:download" />
                                        Download CSV Template
                                    </a>
                                </div>

                                <div className="mb-16">
                                    <label className="form-label fw-semibold" htmlFor="csvFileInputAll">Select CSV File</label>
                                    <input
                                        id="csvFileInputAll"
                                        type="file"
                                        accept=".csv,text/csv"
                                        className="form-control"
                                        onChange={(e) => { setCsvFile(e.target.files[0] || null); setImportResult(null); }}
                                    />
                                    {csvFile && (
                                        <p className="text-sm text-success-main mt-6 mb-0 d-flex align-items-center gap-1">
                                            <Icon icon="mdi:check-circle" />
                                            {csvFile.name} — {(csvFile.size / 1024).toFixed(1)} KB
                                        </p>
                                    )}
                                </div>

                                {importResult && (
                                    <div className={`alert ${importResult.success ? 'alert-success' : 'alert-danger'}`}>
                                        <p className="fw-semibold mb-8">
                                            {importResult.success ? 'Import Complete' : 'Import Failed'}
                                        </p>
                                        {importResult.stats && (
                                            <div className="row g-2 mb-8">
                                                {[
                                                    { label: 'Rows processed', val: importResult.stats.processed },
                                                    { label: 'Owners upserted', val: importResult.stats.owners_upserted },
                                                    { label: 'Venues upserted', val: importResult.stats.venues_upserted },
                                                ].map((s, i) => (
                                                    <div className="col-4" key={i}>
                                                        <div className="bg-base p-8 radius-8 text-center border">
                                                            <div className="fw-bold text-lg">{s.val}</div>
                                                            <div className="text-xs text-secondary-light">{s.label}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {importResult.error && <p className="mb-0">{importResult.error}</p>}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary-600" onClick={() => setShowBulkImport(false)}>Close</button>
                                <button
                                    type="button"
                                    className="btn btn-primary-600 d-flex align-items-center gap-2"
                                    disabled={!csvFile || importLoading}
                                    onClick={async () => {
                                        if (!csvFile) return;
                                        setImportLoading(true); setImportResult(null);
                                        try {
                                            const formData = new FormData();
                                            formData.append('csv', csvFile);
                                            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/import-csv`, { method: 'POST', body: formData });
                                            const data = await res.json();
                                            setImportResult(data);
                                            if (data.success) { fetchMeta(); fetchVenues(); toast.success('CSV import completed!'); }
                                        } catch (err) {
                                            setImportResult({ success: false, error: err.message });
                                            toast.error('Import failed: ' + err.message);
                                        } finally { setImportLoading(false); }
                                    }}
                                >
                                    {importLoading ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Importing...</> : <><Icon icon="mdi:upload" /> Start Import</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllVenuesLayer;
